import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Gift, Copy, Check, ArrowLeft, Loader2 } from 'lucide-react';
import Button from '../components/ui/Button';
import ThemeToggle from '../components/ThemeToggle';
import { useAuthStore } from '../store/auth-store';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const GiftPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const [giftCode, setGiftCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const plan = location.pathname.includes('premium') ? 'premium' : 'pro';

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const handleGiftCreation = async () => {
      try {
        setIsLoading(true);

        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const paymentId = urlParams.get('payment_id');
        const referer = document.referrer;

        if (!paymentId || !referer.includes('vypay.xyz')) {
          toast.error('Invalid payment verification');
          navigate('/pricing');
          return;
        }

        // Create payment verification record
        const { data: payment, error: paymentError } = await supabase
          .from('payment_verifications')
          .insert([{
            payment_id: paymentId,
            user_id: user.id,
            amount: plan === 'premium' ? 49.99 : 19.99,
            plan,
            status: 'pending'
          }])
          .select()
          .single();

        if (paymentError) throw paymentError;

        // Generate gift code
        const { data: code, error: giftError } = await supabase
          .rpc('create_gift_from_payment', {
            payment_id_param: paymentId,
            user_id_param: user.id
          });

        if (giftError) throw giftError;

        setGiftCode(code);
        toast.success('Gift code created successfully!');
      } catch (error: any) {
        console.error('Gift creation error:', error);
        toast.error('Failed to create gift code');
        navigate('/pricing');
      } finally {
        setIsLoading(false);
      }
    };

    handleGiftCreation();
  }, [user, navigate, plan]);

  const handleCopy = async () => {
    if (!giftCode) return;

    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/redeem?code=${giftCode}`
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Gift link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-purple-500/5 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Creating your gift code...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-purple-500/5">
      <div className="fixed top-0 left-0 right-0 border-b bg-background/80 backdrop-blur-sm z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/pricing')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Pricing
          </Button>
          <ThemeToggle />
        </div>
      </div>

      <div className="container mx-auto px-4 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto"
        >
          <div className="relative overflow-hidden rounded-2xl border bg-card/50 backdrop-blur-sm p-8 shadow-xl">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-primary/10 opacity-10" />
            
            <div className="relative text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-primary/10 flex items-center justify-center"
              >
                <Gift className="w-8 h-8 text-primary" />
              </motion.div>

              <h1 className="text-2xl font-bold mb-2">
                Gift Created Successfully!
              </h1>
              
              <p className="text-muted-foreground mb-6">
                Share this gift code with someone special to give them
                CodeNest {plan.charAt(0).toUpperCase() + plan.slice(1)}
              </p>

              {giftCode && (
                <div className="space-y-4">
                  <div className="relative">
                    <div className="p-4 rounded-lg bg-muted/50 font-mono text-sm break-all">
                      {window.location.origin}/redeem?code={giftCode}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCopy}
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    This gift code will expire in 30 days
                  </p>

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/gifts')}
                  >
                    View My Gift Codes
                  </Button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GiftPage;